import { Injectable } from '@angular/core';
import { Restangular } from 'ngx-restangular';
import { AppSettings } from '../../app.settings';
import { RadiusService } from '../radius/radius.service';
import { UsersService } from '../users/users.service';
import Handlebars from 'handlebars/dist/cjs/handlebars';
import { BrandsService } from '../brands/brands.service';
import { CryptoService } from '../crypto/crypto.service';
import { Platform } from '@ionic/angular';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastComponent } from '../../components/toast/toast.component';
import { UiAlertService } from '../ui-alert/ui-alert.service';
import { StripeService } from '../stripe/stripe.service';
import { Location } from '@angular/common';
import { VendorsService } from '../vendors/vendors.service';
import { NotificationsService } from '../notifications/notifications.service';
import { CognitoUserPool, CognitoUser, AuthenticationDetails } from 'amazon-cognito-identity-js';
import { BrowserService } from '../browser/browser.service';
import { UtilsService } from '../utils/utils.service';
import { TeamsService } from '../teams/teams.service';

export interface Product {
  id: string;
  vendorName: string;
  productName: string;
  vendorSlug: string;
  productSlug: string;
  stripeProductId: string;
  stripeVendor: string;
  metadata: {
    enroll: {
      planCode: string;
      groupName: string;
      groupNumber: string;
      partnerName: string;
    };
    reward: {
      id: string;
    };
    vendorShortNames: string[];
    [key: string]: any;
  };
  feedConfig: any | null;
  createdAt: string;   // ISO date string
  modifiedAt: string;  // ISO date string
  name: string;
}

export interface ItemAction {
  type: string;
  amount: number;
  arguments: {
    products: {
      amount: string;
    };
    quantity: string;
    launchUrl: string;
    vendorProductId: string;
  };
  hideOnPurchased: boolean;
  isGroup?: boolean;
}

export interface ItemActions {
  [productId: string]: ItemAction;
}

export interface PaymentMethodOption {
  value: string;
  label: string;
  type: 'radio';
}

export interface UserPaymentInfo {
  userId: string;
  defaultPaymentMethod: string;   // Stripe PaymentMethod ID (e.g. "pm_...")
  stripeCustomerId: string;       // Stripe Customer ID (e.g. "cus_...")
  createdAt: string;              // ISO date string
}

@Injectable({
  providedIn: 'root'
})
export class RewardsService {

  static enrolling = false;

  static Reward = {
    FIRST_GOAL: 'firstGoal',
    COMPLETE_FIRST_TASK: 'completeFirstTask',
    ASSIGN_TASK: 'assignTask',
    CONNECT_BANK_ACCOUNT: 'connectBankAccount',
    ADD_CHILD: 'addChild',
    HALFWAY_GOAL: 'halfwayGoal',
    COMPLETE_GOAL: 'completeGoal'
  };

  CACHING = false;

  private rewards;

  constructor(
    private restangular: Restangular,
    private radiusService: RadiusService,
    private brandsService: BrandsService,
    private platform: Platform,
    private cryptoService: CryptoService,
    private usersService: UsersService,
    private toastComponent: ToastComponent,
    private router: Router,
    private uiAlertService: UiAlertService,
    private stripeService: StripeService,
    private location: Location,
    private vendorsService: VendorsService,
    private notificationsService: NotificationsService,
    private browserService: BrowserService,
    private utilsService: UtilsService,
    private teamsService: TeamsService,
  ) { }

  private rewardParams = {
    brand: this.brandsService.getCampaignBrand()
  };

  getRewards(userId: string, readFromCache: boolean = false, dontFilter: boolean = false): Promise<any> {
    if (readFromCache && this.rewards) {
      return Promise.resolve(this.rewards);
    }
    return this.restangular.one('v3').all('rewards').customGETLIST('', this.rewardParams).toPromise().then(rewards => {
      rewards = this.utilsService.uniqWith(rewards, (a: any, b: any) => a.id === b.id);
      if (!userId) {
        return rewards;
      }
      return dontFilter ? rewards : this.filterOutClaimedRewards(userId, rewards);
    });
  }

  getLocalRewards(userId: string, latitude: number, longitude: number): Promise<any> {
    return this.restangular.one('v3').one('rewards', userId).one('latitude', latitude).one('longitude', longitude).customGETLIST('', this.rewardParams).toPromise().then(rewards => {
      return this.utilsService.uniqWith(rewards, (a: any, b: any) => a.id === b.id);
    });
  }

  getPublicRewards(): Promise<any> {
    return this.restangular.one('v3').one('rewards').all('public').customGETLIST('', this.rewardParams).toPromise().then(rewards => {
      return this.utilsService.uniqWith(rewards, (a: any, b: any) => a.id === b.id);
    });
  }

  private filterOutClaimedRewards(userId, rewards): Promise<any> {
    // TODO: DO THIS ON THE SERVER SIDE
    return this.getEarnedRewards(userId).then(earnedRewards => {
      const result = rewards.filter(reward => (reward.schedule && +reward.schedule.count === 0) || !earnedRewards.find(earnedReward => earnedReward.rewardId === reward.id && earnedReward.status === 'claimed'));
      return result;
    });
  }

  getReward(userId: string, rewardId: string): Promise<any> {
    return this.getRewards(userId, true).then(rewards => {
      return rewards.find(thisReward => rewardId === thisReward.id);
    });
  }

  findReward(userId: string, rewardName: string): Promise<any> {

    if (!this.brandsService.getUIConfig().snacks) {
      return Promise.resolve(undefined);
    }

    return this.getRewards(userId, true).then(rewards => {
      return this.filterOutClaimedRewards(userId, rewards).then(filteredRewards => {
        return filteredRewards.find(thisReward => rewardName === thisReward.name.split(' ')[0]);
      });
    });
  }

  presentReward(userId: string, rewardId: string): Promise<any> {
    return this.getRewards(userId, true).then(rewards => {
      const foundReward = rewards.find(thisReward => rewardId === thisReward.id);
      if (foundReward) {
        let amount;
        switch (foundReward.rewardDetails.amount.type) {
          case 'fixed':
            amount = foundReward.rewardDetails.amount.value;
            break;
          case 'randomLinear':
            amount = Math.random() * (foundReward.rewardDetails.amount.highValue -
              foundReward.rewardDetails.amount.lowValue) + foundReward.rewardDetails.amount.lowValue;
            if (amount.roundTo) {
              amount = Math.round(amount / foundReward.rewardDetails.amount.roundTo) * foundReward.rewardDetails.amount.roundTo;
            }
            break;
        }
        this.restangular.one('v3').one('rewards', userId).all('earned').customPOST({
          status: 'presented',
          parameters: { amount: amount },
          rewardId: foundReward.id
        });
      }
      return Promise.resolve(foundReward);
    });
  }

  getUserRewardCount(userId: string, rewardName: string): Promise<number> {
    return this.getRewards(userId, true).then(rewards => {
      const foundReward = rewards.find(thisReward => rewardName === thisReward.name);
      if (foundReward) {
        return this.restangular.one('v3').one('user', userId).one('reward', foundReward.id).one('count').get().toPromise();
      }
      return Promise.resolve(-1);
    });
  }

  getGlobalRewardCount(userId: string, rewardName: string): Promise<number> {
    return this.getRewards(userId, true).then(rewards => {
      const foundReward = rewards.find(thisReward => rewardName === thisReward.name);
      if (foundReward) {
        return this.restangular.one('v3').one('reward', foundReward.id).one('count').get().toPromise();
      }
      return Promise.resolve(-1);
    });
  }

  addFormDataToReward(userId: string, rewardName: string, formData: any): Promise<any> {
    return this.getRewards(userId, true).then(rewards => {
      const foundReward = rewards.find(thisReward => rewardName === thisReward.name);
      if (foundReward) {
        this.restangular.one('v3').one('rewards', userId).all('earned').customPATCH({
          formData: formData,
          rewardId: foundReward.id
        });
      }
      return Promise.resolve(foundReward);
    });
  }

  claimReward(familyId: string, userId: string, rewardId: string): Promise<any> {
    return this.claimRewardHelper(familyId, userId, rewardId, {});
  }

  claimGoalReward(familyId: string, userId: string, rewardId: string, goalId: string): Promise<any> {
    return this.claimRewardHelper(familyId, userId, rewardId, { goalId: goalId });
  }

  claimTaskReward(familyId: string, userId: string, rewardId: string, taskId: string): Promise<any> {
    return this.claimRewardHelper(familyId, userId, rewardId, { taskId: taskId });
  }

  private claimRewardHelper(familyId: string, userId: string, rewardId: string, args: any): Promise<any> {
    return this.getRewards(userId, false, true).then(rewards => {
      const foundReward = rewards.find(thisReward => rewardId === thisReward.id);
      if (foundReward) {
        const patchPayload = Object.assign({}, {
          status: 'claimed',
          rewardId: foundReward.id
        }, args);
        // TODO: THIS MUST BE DONE ON THE SERVER
        return this.restangular.one('v3').one('rewards', userId).all('earned').customPOST(patchPayload).then(info => {
          return this.radiusService.reloadAccount(familyId, userId, info.amount, AppSettings.getRewardsInfo().wallitCounterParty);
        });
      }
      return Promise.resolve(foundReward);
    });
  }

  getEarnedRewards(userId: string): Promise<any> {
    return this.restangular.one('v3').one('rewards', userId).all('earned').getList().toPromise().then(rewards => {
      return rewards;
    });
  }

  getRewardTransactions(familyId: string, userId: string): Promise<any> {
    // return this.restangular.one('families', familyId).one('users', userId).one('rewards').one('campaigns').all('transactions').getList().toPromise();
    return this.restangular.one('families', familyId).one('users', userId).one('rewards', 'x').one('available-campaigns').getList().toPromise();
  }

  claimRewardTransaction(familyId: string, userId: string, claimId: string): Promise<any> {
    const body = {
      claimed: true
    };
    return this.restangular.one('families', familyId).one('users', userId).one('rewards').one('campaigns').all('transactions', claimId).customPUT(body).toPromise();
  }

  getRewardActionFromStripeProductId(userId: string, productId: string): Promise<any> {
    return this.restangular.one('v3').one('rewards', userId).one('productid', productId).one('getpurchaseaction').get().toPromise();
  }

  getRewardFromStripeProductId(userId: string, productId: string): Promise<any> {
    return this.restangular.one('v3').one('rewards', userId).one('productid', productId).one('getreward').get().toPromise();
  }

  getRewardFromVendorProductId(userId: string, vendorProductId: string): Promise<any> {
    return this.restangular.one('v3').one('rewards', userId).one('vendorproductid', vendorProductId).one('getreward').get().toPromise();
  }

  checkoutCart(
    cartItems: Product[],
    itemActions: ItemActions,
    totalPrice: number,
    doneCallback: Function,
    startCallback: Function
  ) {
    this.stripeService.getUserPaymentInfo().then(async (userPaymentInfo: UserPaymentInfo) => {
      if (!userPaymentInfo.defaultPaymentMethod) {
        this.uiAlertService.presentAlertConfirm(this.uiAlertService.paymentMessage()).then(confirm => {
          if (confirm) {
            this.router.navigate(['/products']);
          }
        });
        return;
      }

      const paymentMethods: PaymentMethodOption[] = await this.getAvailablePaymentMethods();

      if (paymentMethods.length > 1) {
        this.uiAlertService.presentAlertPaymentMethodConfirm(
          `This will initiate a recurring charge of $${totalPrice.toFixed(2)}.<p>You may cancel your subscriptions at any time from the Payment Methods & History area of the Products tab.</p>Transactions will appear on your statement as 'Wallit'. Proceed?`,
          paymentMethods
        ).then(result => {
          let { role, value } = result;
          if (role === 'confirm') {
            let teamId = null;
            if (value !== 'wallit' && value !== 'stripe') {
              teamId = value;
              value = 'allowance';
            }
            startCallback?.();
            for (const cartItem of cartItems) {
              this.initiateCartItemPurchase(
                cartItem,
                itemActions,
                userPaymentInfo,
                doneCallback,
                value,
                teamId,
              );
            }
          }
        });
      } else {
        this.uiAlertService.presentAlertConfirm(
          `This will initiate a recurring charge of $${totalPrice.toFixed(2)}.<p>You may cancel your subscriptions at any time from the Payment Methods & History area of the Products tab.</p>Transactions will appear on your statement as 'Wallit'. Proceed?`
        ).then(confirm => {
          if (confirm) {
            startCallback?.();
            for (const cartItem of cartItems) {
              this.initiateCartItemPurchase(
                cartItem, 
                itemActions, 
                userPaymentInfo, 
                doneCallback, 
                'stripe'
              );
            }
          }
        });
      }
    });
  }

  initiateCartItemPurchase(
    cartItem: Product, 
    itemActions: ItemActions, 
    userPaymentInfo: UserPaymentInfo, 
    doneCallback: Function, 
    paymentMethodValue: string, 
    teamId: string = null
  ) {
    const cartItemAction = itemActions[cartItem.id];
    const productId = cartItem.stripeProductId;
    const productCount = 1;
    const amount = cartItemAction.isGroup ? cartItemAction.amount : cartItemAction.arguments.products?.amount;
    this.stripeService.purchaseSubscription(
      userPaymentInfo.stripeCustomerId,
      productId,
      productCount,
      true,
      amount ?? null,
      cartItem.vendorName,
      cartItem.id,
      paymentMethodValue,
      {},
      teamId
    ).then(result => {
      if (!result.error && !result.errors) {
        if (cartItemAction.amount) {
          const paymentMethodMessage = paymentMethodValue === 'stripe' 
            ? 'and your payment method charged' 
            : 'using team allowance balance';
          this.toastComponent.presentToast(`${cartItem.name} has been purchased ${paymentMethodMessage}.`);
        } else {
          this.toastComponent.presentToast(`${cartItem.name} has been purchased.`);
        }
        setTimeout(() => {
          this.notificationsService.updateNotifications();
        }, 1000);
      }
      this.stripeService.notifyProductsPurchasedChanged(productId);
      if (doneCallback) {
        doneCallback(cartItem.id);
      } else {
        this.location.back();
      }
    }).catch(error => this.toastComponent.presentToast('Your product purchase failed.'));
  }

  async getAvailablePaymentMethods(): Promise<any> {
    const myWallit = await this.usersService.myWallit();
    const wallitBalance = myWallit.wallitBalance;
    const teams = await this.teamsService.getUserMemberTeams(this.usersService.me().user.id);
    const allowances = teams.filter(t => t.categories.some(cat => ['Insurance', 'Health & Wellness'].includes(cat))).map(t => {
      return {
        value: t.id,
        label: `${t.name} (${t.balance ? '$' + t.balance : 'No Balance'})`,
        type: 'radio'
      };
    });
    const accounts = [
      // ...myWallit.accounts.filter(a => a.fundingSourceType === 'balance'),
      ...allowances,
      { label: 'Default Payment Method', value: 'stripe', type: 'radio' },
    ];
    return accounts;
  }

  purchaseProduct(productId, count, totalPrice, action, doneCallback, vendor: string = null, onCancelPurchase = null) {
    console.log('PURCHASEPRODUCT')
    if (count === 0 || count === '0') {
      if (doneCallback) {
        doneCallback();
      }
      return;
    }
    this.stripeService.getUserPaymentInfo().then(async userPaymentInfo => {
      if (action.isGroup) {
        totalPrice = parseFloat(action.amount);
      } else {
        if (action.arguments.products?.amount) {
          totalPrice = parseFloat(action.arguments.products?.amount);
        }
      }
      if (totalPrice === 0 || totalPrice === '0') {
        if (doneCallback) {
          doneCallback();
        }
        return;
      }
      if (!userPaymentInfo.defaultPaymentMethod) {
        this.uiAlertService.presentAlertConfirm(this.uiAlertService.paymentMessage()).then((confirm) => {
          if (confirm) {
            this.router.navigate(['/products']);
          } else {
            onCancelPurchase?.();
          }
        });
        return;
      }
      const paymentMethods = await this.getAvailablePaymentMethods();
      if (paymentMethods.length > 1) {
        this.uiAlertService.presentAlertPaymentMethodConfirm(`This will initiate a recurring charge of $${totalPrice.toFixed(2)}.<p>You may cancel your subscription at any time from the Payment Methods & History area of the Products tab.</p>Transactions will appear on your statement as 'Wallit'. ${action.arguments.enrollQuestion ? '<p>' + action.arguments.enrollQuestion + '</p>' : ''} Proceed?`, paymentMethods).then(result => {
          let { role, value } = result;
          if (role === 'confirm') {
            let teamId = null;
            if (value !== 'wallit' && value !== 'stripe') {
              teamId = value;
              value = 'allowance';
            }
            this.initiatePurchase(userPaymentInfo, productId, count, totalPrice, action, doneCallback, vendor, value, teamId);
          } else {
            onCancelPurchase?.();
          }
        });
      } else {
        this.uiAlertService.presentAlertConfirm(`This will initiate a recurring charge of $${totalPrice.toFixed(2)}.<p>You may cancel your subscription at any time from the Payment Methods & History area of the Products tab.</p>Transactions will appear on your statement as 'Wallit'. ${action.arguments.enrollQuestion ? '<p>' + action.arguments.enrollQuestion + '</p>' : ''} Proceed?`).then(confirm => {
          if (confirm) {
            this.initiatePurchase(userPaymentInfo, productId, count, totalPrice, action, doneCallback, vendor, 'stripe', null);
          } else {
            onCancelPurchase?.();
          }
        });
      }
    });
  }

  async initiatePurchase(userPaymentInfo, productId, count, totalPrice, action, doneCallback, vendor: string = null, paymentMethod: string = null, teamId: string = null) {
    const amount = action.isGroup ? action.amount : action.arguments.products?.amount;
    const args = Object.assign({}, { action }, action.arguments);
    this.stripeService.purchaseSubscription(
      userPaymentInfo.stripeCustomerId,
      productId,
      count,
      true,
      amount,
      vendor,
      args.vendorProductId,
      paymentMethod,
      args,
      teamId
    ).then(result => {
      if (!result.error && !result.errors) {
        if (paymentMethod === 'allowance') {
          this.toastComponent.presentToast('Product purchase initiated using team allowance balance.');
        } else {
          this.toastComponent.presentToast('Product purchase initiated.');
        }
        setTimeout(() => {
          this.notificationsService.updateNotifications();
        }, 1000);
      }
      this.stripeService.notifyProductsPurchasedChanged(productId);
      if (doneCallback) {
        doneCallback();
      } else {
        this.location.back();
      }
    }).catch(error => this.toastComponent.presentToast('Your product purchase failed.'));
  }

  gotoUrl(url, iFrameAllowed) {
    console.log('GOTOURL', url, iFrameAllowed)
    if (this.platform.is('cordova')) {
      this.browserService.open(url);
    } else {
      if (iFrameAllowed) {
        this.router.navigate(['embeddedpage', this.cryptoService.encodeJSON({ url })]);
      } else {
        this.browserService.open(url);
        // window.location.href = url;
      }
    }
  };

  gotoHtml(html) {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(html);
      newWindow.document.close();
    }
    // this.router.navigate(['embeddedpage', this.cryptoService.encodeJSON({html})]);
  }

  afterPurchase(action, offer = {}) {
    let template;
    if (!action.purchased) {
      action.purchased = {};
    }
    if (!action.purchased.destination) {
      action.purchased.destination = { type: action.type, value: action.arguments.url };
    }
    switch (action.purchased?.destination?.type) {
      case 'externalurl':
        template = Handlebars.compile(action.purchased.destination.value);
        window.open(template(Object.assign({}, this.usersService.me(), { reward: offer }, { platform: this.platform.platforms() })));
        return true;
      case 'embedded':
        template = Handlebars.compile(action.purchased.destination.value);
        this.gotoUrl({ url: template(Object.assign({}, this.usersService.me(), {system: {origin: window.location.origin}}))}, true);
        return true;
      default:
        this.vendorsService.getProductLaunchMetadata(action.arguments.vendorProductId).then(launchInfo => {
          if (launchInfo.cognito) {
            const userPool = new CognitoUserPool(launchInfo.cognito);
            const authenticationDetails = new AuthenticationDetails({
              Username: launchInfo.user.name,
              Password: launchInfo.user.password,
            });
            const userData = {
              Username: launchInfo.user.name,
              Pool: userPool,
            };
            const cognitoUser = new CognitoUser(userData);
            cognitoUser.authenticateUser(authenticationDetails, {
              onSuccess: () => {
                setTimeout(() => {
                  if (!action.purchased?.autoLaunch && RewardsService.enrolling) {
                    return true;
                  }
                  this.gotoUrl(launchInfo.url, action.arguments.iframe);
                }, 200);
              },
              onFailure: () => {
                console.log('COGNITO AUTH FAIL');
              }
            });
          } else if (launchInfo.error) {
            this.toastComponent.presentToast(`Product launch failed: ${launchInfo.error}`);
          } else {
            console.log('AUTOLAUNCH', action.purchased?.autoLaunch, RewardsService.enrolling);
            if (!action.purchased?.autoLaunch && RewardsService.enrolling) {
              return true;
            }
            const url = action.arguments.launchUrl || launchInfo.url;
            if (url) {
              if (url.startsWith('http')) {
                this.gotoUrl(url, action.arguments.iframe);
              } else {
                const template = Handlebars.compile(url);
                this.router.navigate([template(this.usersService.me())]);
              }
            } else {
              this.gotoHtml(launchInfo.html);
            }
          }
        }).catch(error => {
          this.toastComponent.presentToast(error.error.error);
        });
    }
    return false;
  }

}
